import React, { FC, useMemo, useCallback } from "react";
import {
    ReactFlow,
    Node,
    Edge,
    Controls,
    MiniMap,
    Background,
    BackgroundVariant,
    useNodesState,
    useEdgesState,
    MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Box } from "@mui/material";
import dagre from "dagre";
import { StoryNavigationNode } from "common/build/api-parameters/story";
import { isAutoContinueDecisionPoint } from "common/build/utils/branchedStoryUtils";
import StoryPartNode, { StoryPartNodeData } from "./StoryPartNode";

const nodeTypes = {
    storyPart: StoryPartNode,
};

// Layout configuration
const dagreGraph = new dagre.graphlib.Graph();
dagreGraph.setDefaultEdgeLabel(() => ({}));

const nodeWidth = 180;
const nodeHeight = 100;

interface BranchedStoryFlowChartDiagramProps {
    parts: StoryNavigationNode[];
    onPartClick?: (partNumber: number) => void;
    activePartNumber?: number | null;
}

// Function to apply dagre layout
const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
    dagreGraph.setGraph({ rankdir: "TB", ranksep: 80, nodesep: 60 });

    nodes.forEach((node) => {
        dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight });
    });

    edges.forEach((edge) => {
        dagreGraph.setEdge(edge.source, edge.target);
    });

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        return {
            ...node,
            position: {
                x: nodeWithPosition.x - nodeWidth / 2,
                y: nodeWithPosition.y - nodeHeight / 2,
            },
        };
    });

    return { nodes: layoutedNodes, edges };
};

// Transform story data to React Flow nodes and edges
const transformStoryData = (
    parts: StoryNavigationNode[],
    onPartClick?: (partNumber: number) => void,
    activePartNumber?: number | null,
): { nodes: Node[]; edges: Edge[] } => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    // Add start node
    nodes.push({
        id: "start",
        type: "storyPart",
        position: { x: 0, y: 0 },
        data: {
            partLabel: "Start",
            nodeType: "start",
            isActive: false,
        },
    });

    // Add story part nodes
    parts.forEach((part) => {
        // Filter out auto-continues (a single branch text "Continue")
        const realDecisionPoints = (part.decisionPoints || []).filter((dp) => !isAutoContinueDecisionPoint(dp));
        const continuations = (part.decisionPoints || []).filter((dp) => isAutoContinueDecisionPoint(dp));

        const isDecisionPoint = realDecisionPoints.length > 0;

        nodes.push({
            id: `part-${part.partNumber}`,
            type: "storyPart",
            position: { x: 0, y: 0 }, // Will be calculated by dagre
            data: {
                partNumber: part.partNumber,
                partLabel: part.partLabel || `Part ${part.partNumber}`,
                nodeType: "story",
                questionCount: part.questions?.length || 0,
                decisionPointCount: realDecisionPoints.length,
                isActive: activePartNumber === part.partNumber,
                onClick: () => onPartClick?.(part.partNumber),
            },
        });

        // Add decision point nodes (diamond shaped) for REAL decisions
        if (isDecisionPoint) {
            realDecisionPoints.forEach((dp, dpIndex) => {
                const decisionId = `decision-${part.partNumber}-${dpIndex}`;
                // Extract branch labels (A/B, C/D, E/F)
                const branchLabels = dp.branches.map((b) => b.branchLabel).join("/");
                const shortQuestion = dp.decisionText.substring(0, 25) + "...";

                nodes.push({
                    id: decisionId,
                    type: "storyPart",
                    position: { x: 0, y: 0 },
                    data: {
                        partLabel: `${shortQuestion} (${branchLabels})`,
                        nodeType: "decision",
                    },
                });

                // Edge from part to decision point
                edges.push({
                    id: `edge-part${part.partNumber}-to-${decisionId}`,
                    source: `part-${part.partNumber}`,
                    target: decisionId,
                    type: "smoothstep",
                    animated: false,
                });

                // Edges from decision point to branch targets
                dp.branches.forEach((branch) => {
                    edges.push({
                        id: `edge-${decisionId}-to-part${branch.targetPartNumber}`,
                        source: decisionId,
                        target: `part-${branch.targetPartNumber}`,
                        type: "smoothstep",
                        label: `${branch.branchLabel}: ${branch.choiceText.substring(0, 20)}...`,
                        labelStyle: {
                            fontSize: 11,
                            fontWeight: "bold",
                        },
                        labelBgStyle: {
                            fill: "#ffffff",
                            fillOpacity: 0.9,
                        },
                        markerEnd: {
                            type: MarkerType.ArrowClosed,
                            width: 20,
                            height: 20,
                            color: "#1FAF8C",
                        },
                        style: {
                            strokeWidth: 2,
                            stroke: "#1FAF8C",
                        },
                    });
                });
            });
        }

        // Add direct edges for auto-continues
        continuations.forEach((cont) => {
            cont.branches.forEach((branch) => {
                edges.push({
                    id: `edge-part${part.partNumber}-to-part${branch.targetPartNumber}`,
                    source: `part-${part.partNumber}`,
                    target: `part-${branch.targetPartNumber}`,
                    type: "smoothstep",
                    markerEnd: {
                        type: MarkerType.ArrowClosed,
                        width: 20,
                        height: 20,
                        color: "#64748b",
                    },
                    style: {
                        strokeWidth: 2,
                        stroke: "#64748b",
                    },
                });
            });
        });
    });

    // Add end node
    nodes.push({
        id: "end",
        type: "storyPart",
        position: { x: 0, y: 0 },
        data: {
            partLabel: "End",
            nodeType: "end",
            isActive: false,
        },
    });

    // Connect start to part 1
    if (parts.length > 0) {
        edges.push({
            id: "edge-start-to-part1",
            source: "start",
            target: "part-1",
            type: "smoothstep",
            markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
                color: "#64748b",
            },
            style: {
                strokeWidth: 2,
                stroke: "#64748b",
            },
        });
    }

    // Connect all end nodes (parts without any decision points or continuations) to end
    const endParts = parts.filter((p) => !p.decisionPoints || p.decisionPoints.length === 0);
    endParts.forEach((part) => {
        edges.push({
            id: `edge-part${part.partNumber}-to-end`,
            source: `part-${part.partNumber}`,
            target: "end",
            type: "smoothstep",
            markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
                color: "#64748b",
            },
            style: {
                strokeWidth: 2,
                stroke: "#64748b",
            },
        });
    });

    return { nodes, edges };
};

export const BranchedStoryFlowChartDiagram: FC<BranchedStoryFlowChartDiagramProps> = ({
    parts,
    onPartClick,
    activePartNumber,
}) => {
    const { nodes: transformedNodes, edges: transformedEdges } = useMemo(
        () => transformStoryData(parts, onPartClick, activePartNumber),
        [parts, onPartClick, activePartNumber],
    );

    const { nodes: layoutedNodes, edges: layoutedEdges } = useMemo(
        () => getLayoutedElements(transformedNodes, transformedEdges),
        [transformedNodes, transformedEdges],
    );

    const [nodes, setNodes, onNodesChange] = useNodesState(layoutedNodes);
    const [edges, setEdges, onEdgesChange] = useEdgesState(layoutedEdges);

    // Update nodes when layout changes
    React.useEffect(() => {
        setNodes(layoutedNodes);
        setEdges(layoutedEdges);
    }, [layoutedNodes, layoutedEdges, setNodes, setEdges]);

    const onNodeClick = useCallback(
        (_event: React.MouseEvent, node: Node) => {
            const partMatch = node.id.match(/^part-(\d+)$/);
            if (partMatch && onPartClick) {
                const partNumber = parseInt(partMatch[1], 10);
                onPartClick(partNumber);
            }
        },
        [onPartClick],
    );

    return (
        <Box style={{ width: "100%", height: "600px", border: "1px solid #e0e0e0", borderRadius: "8px" }}>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodeClick={onNodeClick}
                nodeTypes={nodeTypes}
                fitView
                fitViewOptions={{ padding: 0.2 }}
                minZoom={0.5}
                maxZoom={1.5}
            >
                <Background variant={BackgroundVariant.Dots} gap={16} size={1} color="#e0e0e0" />
                <Controls />
                <MiniMap
                    nodeColor={(node) => {
                        const nodeData = node.data as StoryPartNodeData;
                        if (nodeData && nodeData.nodeType) {
                            switch (nodeData.nodeType) {
                                case "start":
                                    return "#10B981";
                                case "decision":
                                    return "#3B82F6";
                                case "end":
                                    return "#EF4444";
                                default:
                                    return "#F59E0B";
                            }
                        }
                        return "#F59E0B";
                    }}
                    maskColor="rgba(0, 0, 0, 0.1)"
                />
            </ReactFlow>
        </Box>
    );
};

// Display name for debugging
BranchedStoryFlowChartDiagram.displayName = "BranchedStoryFlowChartDiagram";
